// 🏢 @@ 조직 멘션 (2026-09-04 원격 "맨션에서 @@ 2개를 붙이면 해당 대화방/토픽방에 있는 부서/부문을 표시하고 멘션")
//   방 멤버의 whois(회사·부문·부서·겸직부서) + 조직도 조상(상위 조직 전체)을 묶어 "이 방에 있는 조직" 목록을 만든다.
//   순수 함수 — 컴포저 훅·스레드 패널·알림 판정·렌더·테스트가 함께 쓴다.
//   🔴 2026-09-10 사용자 "@@ 는 인원 이름이 아니라 부서명으로 · 상위부서는 하위부서 전체 포함 · 몇 명인지 표시":
//      선택 시 본문에는 **토큰 하나 `@@조직명(N명)`** 만 들어간다(종전 `@이름 @이름 …` 펼침 폐지). 괄호 인원 표기가 종결자라
//      조직명에 공백이 있어도 경계가 선다. 이 토큰을 렌더(lib/markdown mentionify)·입력창 색칠(mentionSpans)·번역 보호(mentionNames)·
//      알림 판정(useRealtimeNotify → hasDeptMentionFor)·서버 '오늘의 멘션'(presence.ts `@라벨\(`) 이 같은 규칙으로 인식한다.
//      새 인식 지점을 만들면 matchDeptMentionAt 을 쓸 것 — 정규식을 복제하지 않는다.
import type { GwWhois } from '../api/rcClient';
import type { OrgDept } from '@tpt/design';
// 🔴 이 파일은 런타임 import 0(type import 만) — check-dept-mention.mjs 가 node 타입 스트립으로 직접 import 한다.
// (초성 유틸 미사용 — 부서 라벨은 부분일치만, 2026-09-08)

export type DeptKind = 'company' | 'division' | 'team' | 'job';
export interface DeptGroup {
	/** `${kind}:${label}` — 드롭다운 항목 키 */
	key: string;
	kind: DeptKind;
	label: string;
	/** 이 조직에 속한 방 멤버 username (본인 제외, memberNames 순서). 상위 조직이면 하위 조직 인원까지 */
	members: string[];
	/** 하위 조직(조직도 자손·whois 상위부서 관계)을 통해 들어온 인원이 있다 — 드롭다운 부제 '하위 조직 포함' */
	includesChildren?: boolean;
}

/** 드롭다운 항목 username 접두 — 실사용자 username 과 충돌하지 않는 `~` 로 시작 (mentionNameMap 의 `~alias:` 와 같은 규약) */
export const DEPT_MENTION_PREFIX = '~dept:';
/** `@` 목록 상단의 "@@ 조직" 진입 항목 */
export const DEPT_MENTION_ENTRY = '~dept';

const KIND_ORDER: Record<DeptKind, number> = { company: 0, division: 1, team: 2, job: 3 };

/**
 * 조직도 트리 → rcUsername 별 조상 조직 이름 목록(위→아래). 루트·회사 노드(`company:` / id 없음)와 **본인 소속 노드는 제외**
 * (회사는 whois company, 본인 부서는 whois deptName 이 맡는다). 겸직으로 여러 노드에 있으면 합집합.
 * 상위 조직을 고르면 하위 전체가 포함되게 하는 재료 — whois 의 2단(부서·상위부서)만으로는 부문 > 실 > 팀 3단이 잡히지 않는다.
 */
export function orgAncestorsByUser(root: OrgDept): Map<string, string[]> {
	const out = new Map<string, string[]>();
	const walk = (n: OrgDept, chain: string[]) => {
		const isCompany = !n.id || n.id.startsWith('company:');
		const isDept = !!n.id && !isCompany && !n.id.startsWith('gw-');
		for (const u of n.users || []) {
			const k = String(u.rcUsername || u.username || '').trim();
			if (!k) continue;
			const prev = out.get(k);
			out.set(k, prev ? [...prev, ...chain.filter((c) => !prev.includes(c))] : [...chain]);
		}
		const next = isDept && n.name?.trim() ? [...chain, n.name.trim()] : chain;
		for (const c of n.children || []) walk(c, next);
	};
	walk(root, []);
	return out;
}

/**
 * 방 멤버를 조직별로 묶는다. whois 가 없는 멤버(GW 미등록)는 어느 조직에도 들지 않는다.
 * 같은 이름의 조직은 종류가 달라도 **한 그룹**(직속 인원 + 하위 인원) — 종류는 가장 위(회사 → 부문 → 부서 → 겸직)로.
 * ancestors(조직도 조상, orgAncestorsByUser)를 주면 상위 조직 그룹에 하위 조직 인원이 모두 들어간다(includesChildren).
 * 결과 정렬: 종류 → 인원 많은 순 → 이름.
 */
export function buildDeptGroups(
	usernames: string[],
	whois: Map<string, GwWhois>,
	excludeUsername?: string | null,
	ancestors?: Map<string, string[]>,
): DeptGroup[] {
	const groups = new Map<string, DeptGroup>();   // label → group
	const add = (kind: DeptKind, label: string | null | undefined, u: string, viaChild = false) => {
		const name = String(label || '').trim();
		if (!name) return;
		let g = groups.get(name);
		if (!g) { g = { key: `${kind}:${name}`, kind, label: name, members: [] }; groups.set(name, g); }
		else if (KIND_ORDER[kind] < KIND_ORDER[g.kind]) { g.kind = kind; g.key = `${kind}:${name}`; }
		if (!g.members.includes(u)) g.members.push(u);
		if (viaChild) g.includesChildren = true;
	};
	for (const u of usernames) {
		if (excludeUsername && u === excludeUsername) continue;
		const w = whois.get(u);
		if (!w?.found) continue;
		const dept = String(w.deptName || '').trim();
		add('company', w.company, u);
		add('division', w.parentDeptName, u, String(w.parentDeptName || '').trim() !== dept);
		add('team', w.deptName, u);
		for (const j of String(w.jobDeptNames || w.jobDeptName || '').split(',')) {
			const jn = j.trim();
			if (jn && jn !== dept) add('job', jn, u);
		}
		for (const a of ancestors?.get(u) || []) if (a !== dept) add('division', a, u, true);
	}
	return Array.from(groups.values())
		.filter((g) => g.members.length > 0)
		.sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || b.members.length - a.members.length || a.label.localeCompare(b.label));
}

/** 검색어(`@@` 뒤 글자)로 조직 목록을 좁힌다 — 라벨 부분일치, 대소문자 무시 */
export function filterDeptGroups(groups: DeptGroup[], q: string): DeptGroup[] {
	const s = q.trim().toLowerCase();
	if (!s) return groups;
	return groups.filter((g) => g.label.toLowerCase().includes(s));   // 부서 라벨은 부분일치만 — 초성은 이름 전용 (2026-09-08 원격 "부서까지 되니 혼란")
}

// ── 본문 토큰 `@@조직명(N명)` ──────────────────────────────────────────────
//   조직명: `@`·개행·괄호 제외 1~60자(공백 허용, 최단일치) · 인원: 숫자 1~4자리 + 단위 문구 ≤20자(`12명` `12 people` `12 người` `12人`).
//   escape 된 HTML(렌더) 위에서도 그대로 맞는다 — 이 문자 집합에 `&lt;` 류가 걸리지 않는다.
const DEPT_TOKEN_RE = /^@@([^@\n()]{1,60}?)\((\d{1,4})([^)\n]{0,20})\)/;
export interface DeptMentionToken { label: string; count: number; /** 토큰 전체 길이(문자) */ length: number; /** 토큰 원문 */ text: string }

/** text 의 at 위치에서 시작하는 `@@조직명(N명)` 토큰. 없으면 null. */
export function matchDeptMentionAt(text: string, at: number): DeptMentionToken | null {
	if (text.charCodeAt(at) !== 64 || text.charCodeAt(at + 1) !== 64) return null;
	const m = DEPT_TOKEN_RE.exec(text.slice(at, at + 96));
	if (!m) return null;
	return { label: m[1].trim(), count: parseInt(m[2], 10), length: m[0].length, text: m[0] };
}

/** 본문에 등장한 조직 토큰 전부(중복 제거, 등장 순) */
export function findDeptMentions(text: string): DeptMentionToken[] {
	if (!text || !text.includes('@@')) return [];
	const out: DeptMentionToken[] = [];
	let i = 0;
	for (;;) {
		const at = text.indexOf('@@', i);
		if (at < 0) break;
		const tk = matchDeptMentionAt(text, at);
		if (tk) { if (!out.some((x) => x.label === tk.label)) out.push(tk); i = at + tk.length; }
		else i = at + 1;
	}
	return out;
}

/** 내 조직 라벨(회사·상위부서·부서·겸직·조직도 조상) 집합으로 — 본문의 어떤 `@@` 토큰이 나를 부르는지 */
export function hasDeptMentionFor(text: string, myLabels: ReadonlySet<string>): boolean {
	if (!myLabels.size) return false;
	return findDeptMentions(text).some((tk) => myLabels.has(tk.label));
}

/**
 * 선택한 조직을 입력창에 넣을 문자열 — `@@조직명(N명) ` (뒤 공백 1개). countLabel 은 호출부가 로케일로 만든 "12명"/"12 people".
 * suffix(멘션 자동 텍스트)는 토큰 뒤에 1회.
 */
export function formatDeptMention(group: DeptGroup, countLabel: string, suffix?: string): string {
	const label = group.label.replace(/[()@\n]/g, ' ').replace(/\s+/g, ' ').trim();   // 토큰 문법 보호 — 라벨의 괄호·@ 는 공백으로
	if (!label || group.members.length === 0) return '';
	const cnt = String(countLabel || String(group.members.length)).replace(/[()\n]/g, '').trim();
	const sfx = suffix && suffix.trim() ? ` ${suffix.trim()}` : '';
	return `@@${label}(${cnt})${sfx} `;
}
